import React, { useRef, useEffect, useCallback, memo } from 'react';

/**
 * EditableSegment - A bulletproof contentEditable component for React
 * 
 * PROBLEM: React re-renders cause cursor to jump in contentEditable elements
 * because updating innerHTML resets the cursor position.
 * 
 * SOLUTION: 
 * 1. Track editing state with a ref flag (not document.activeElement which has timing issues)
 * 2. NEVER update innerHTML while editing - let browser handle all native editing
 * 3. Only sync from props when NOT editing (on blur or when component is not focused)
 * 4. Use memo to prevent unnecessary re-renders
 */
const EditableSegment = memo(({
  html,
  onChange,
  onKeyDown,
  onFocus,
  onBlur,
  onClick,
  className = '',
  placeholder = 'Enter translation...',
  'data-testid': testId,
  style = {},
  readOnly = false,
}) => {
  const contentRef = useRef(null);
  // CRITICAL: Use a ref to track if user is currently editing
  // This is more reliable than checking document.activeElement
  const isEditingRef = useRef(false);
  // Track the last HTML we set, to avoid unnecessary DOM updates
  const lastSetHtmlRef = useRef('');

  // Sync HTML from props ONLY when not editing
  useEffect(() => {
    const element = contentRef.current;
    if (!element) return;

    // NEVER touch innerHTML while user is editing
    if (isEditingRef.current) {
      return;
    }

    // Only update if the HTML actually changed
    const newHtml = html || '';
    if (lastSetHtmlRef.current !== newHtml) {
      element.innerHTML = newHtml;
      lastSetHtmlRef.current = newHtml;
    }
  }, [html]);

  // Initialize content on mount
  useEffect(() => {
    const element = contentRef.current;
    if (element) {
      const initialHtml = html || '';
      element.innerHTML = initialHtml;
      lastSetHtmlRef.current = initialHtml;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  // Handle user input - just notify parent, don't sync anything
  const handleInput = useCallback((e) => {
    if (onChange) {
      const text = e.currentTarget.innerText;
      const newHtml = e.currentTarget.innerHTML;
      // Update our tracking ref so we know what's in the DOM
      lastSetHtmlRef.current = newHtml;
      onChange(text, newHtml);
    }
  }, [onChange]);

  // Handle focus - mark as editing
  const handleFocus = useCallback((e) => {
    isEditingRef.current = true;
    if (onFocus) {
      onFocus(e);
    }
  }, [onFocus]);

  // Handle blur - mark as not editing, sync final state
  const handleBlur = useCallback((e) => {
    isEditingRef.current = false;
    const element = e.currentTarget;
    // Update our tracking ref with final state
    lastSetHtmlRef.current = element.innerHTML;
    if (onBlur) {
      onBlur(e);
    }
  }, [onBlur]);

  // Handle keydown - pass through to parent
  const handleKeyDown = useCallback((e) => {
    if (onKeyDown) {
      onKeyDown(e);
    }
  }, [onKeyDown]);

  // Handle click - stop propagation to prevent parent handlers from interfering
  const handleClick = useCallback((e) => {
    e.stopPropagation();
    if (onClick) {
      onClick(e);
    }
  }, [onClick]);

  return (
    <div
      ref={contentRef}
      contentEditable={!readOnly}
      suppressContentEditableWarning
      onInput={readOnly ? undefined : handleInput}
      onKeyDown={readOnly ? undefined : handleKeyDown}
      onFocus={readOnly ? undefined : handleFocus}
      onBlur={readOnly ? undefined : handleBlur}
      onClick={handleClick}
      className={className}
      data-placeholder={readOnly ? '' : placeholder}
      data-testid={testId}
      style={{ 
        outline: 'none', 
        minHeight: '1em',
        cursor: 'text',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        ...style 
      }}
    />
  );
}, (prevProps, nextProps) => {
  // Custom comparison for memo - only re-render if non-content props change
  // We handle html changes in useEffect, so we can skip re-renders for html changes
  // This prevents React from re-rendering the component when parent updates state
  return (
    prevProps.className === nextProps.className &&
    prevProps.placeholder === nextProps.placeholder &&
    prevProps['data-testid'] === nextProps['data-testid'] &&
    prevProps.onChange === nextProps.onChange &&
    prevProps.onKeyDown === nextProps.onKeyDown &&
    prevProps.onFocus === nextProps.onFocus &&
    prevProps.onBlur === nextProps.onBlur &&
    prevProps.onClick === nextProps.onClick &&
    // Deep compare style object
    JSON.stringify(prevProps.style) === JSON.stringify(nextProps.style)
    // Intentionally NOT comparing html - we handle that in useEffect
  );
});

EditableSegment.displayName = 'EditableSegment';

export default EditableSegment;
